import {
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleElement
} from '@patternfly/react-core';
import { GlobeIcon } from '@patternfly/react-icons';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageToggleProps {
  toggleRef: React.Ref<MenuToggleElement>;
  isOpen: boolean;
  onToggle: () => void;
  label: string;
  ariaLabel: string; 
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({
  toggleRef,
  isOpen,
  onToggle,
  label,
  ariaLabel 
}) => (
  <MenuToggle
    ref={toggleRef}
    onClick={onToggle}
    isExpanded={isOpen}
    icon={<GlobeIcon />}
    aria-label={ariaLabel}
    variant="plainText" 
  >
    {label}
  </MenuToggle>
);

const LanguageSwitcher: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);
  const languages = useMemo(() => [
    { code: 'en', name: t('languages.en', 'English') },
    { code: 'es', name: t('languages.es', 'Español') },
    { code: 'fr', name: t('languages.fr', 'Français') },
    { code: 'de', name: t('languages.de', 'Deutsch') },
    { code: 'it', name: t('languages.it', 'Italiano') },
  ], [t]);

  const currentLanguage = useMemo(() =>
    languages.find(lang => i18n.language?.startsWith(lang.code)) || languages[0],
    [i18n.language, languages]);

  const onSelect = useCallback((_event: React.MouseEvent | undefined, value: string | number | undefined) => {
    if (typeof value === 'string') {
      i18n.changeLanguage(value);
      i18n.emit('languageChanged', value);
    }
    setIsOpen(false);
  }, [i18n]);

  const onToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const renderToggle = useCallback((toggleRef: React.Ref<MenuToggleElement>) => (
    <LanguageToggle
      toggleRef={toggleRef}
      isOpen={isOpen}
      onToggle={onToggle}
      label={currentLanguage.name}
      ariaLabel={t('selectLanguage', 'Select language')}
    />
  ), [isOpen, onToggle, currentLanguage.name, t]);

  return (
    <Dropdown
      isOpen={isOpen}
      onSelect={onSelect}
      onOpenChange={(nextOpen: boolean) => setIsOpen(nextOpen)}
      toggle={renderToggle}
      shouldFocusToggleOnSelect
      id="language-switcher-dropdown"
    >
      <DropdownList>
        {languages.map((lang) => (
          <DropdownItem
            value={lang.code}
            key={lang.code}
            isSelected={currentLanguage.code === lang.code}
          >
            {lang.name}
          </DropdownItem>
        ))}
      </DropdownList>
    </Dropdown>
  );
};

export default LanguageSwitcher;

